import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Box,
  Link,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTranslation } from "react-i18next";

const routeTranslationKeys: Record<string, string> = {
  products: "navigation.products",
  admin: "navigation.admin",
  clients: "navigation.clients",
  shipments: "navigation.shipments",
  dashboard: "navigation.dashboard",
  settings: "adminPanel.settings",
  profile: "common.profile",
  orders: "navigation.orders",
  reports: "navigation.reports",
  inventory: "navigation.inventory",
  labels: "navigation.labels",
  documents: "navigation.documents",
  "warehouse-map": "navigation.warehouseMap",
  chat: "navigation.chat",
  notifications: "navigation.notifications",
  create: "breadcrumbs.create",
  edit: "breadcrumbs.edit",
  bundles: "navigation.bundles",
};

const Breadcrumbs: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  if (location.pathname === "/login" || location.pathname === "/") {
    return null;
  }

  const getDisplayName = (name: string, isLast: boolean): string => {
    if (!isNaN(Number(name))) {
      return `${name}`;
    }

    const translationKey = routeTranslationKeys[name.toLowerCase()];
    if (translationKey) {
      return t(translationKey);
    }

    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <Box sx={{ mb: 2, mt: 1 }}>
      <MuiBreadcrumbs
        separator={
          <NavigateNextIcon
            fontSize="small"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              mx: 1,
            }}
          />
        }
        aria-label="nawigacja"
        sx={{
          "& .MuiBreadcrumbs-separator": {
            mx: 1,
            color: (theme) => theme.palette.text.secondary,
          },
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Link
          component={RouterLink}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "white",
            fontWeight: 500,
            "&:hover": {
              textDecoration: "underline",
              opacity: 0.9,
            },
          }}
        >
          {t("common.home")}
        </Link>
        {pathnames.map((name, index) => {
          const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
          const isLast = index === pathnames.length - 1;
          const displayName = getDisplayName(name, isLast);
          const isNonClickableSegment = ["edit", "create"].includes(name.toLowerCase());

          if (isLast || isNonClickableSegment) {
            return (
              <Typography
                key={`${name}-${index}`}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  fontWeight: 400,
                }}
              >
                {displayName}
              </Typography>
            );
          }

          return (
            <Link
              key={`${name}-${index}`}
              component={RouterLink}
              to={routeTo}
              sx={{
                textDecoration: "none",
                color: "white",
                fontWeight: 500,
                "&:hover": {
                  textDecoration: "underline",
                  opacity: 0.9,
                },
              }}
            >
              {displayName}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;
